/**
 * What a crosshair detail page says about its crosshair, built from the import code.
 *
 * Detail pages used to differ from each other only by name and code — every other line
 * was the same template — and Yandex started folding different crosshairs into one
 * another as duplicates (xeppaa → go, new-tenz → lowel). Decoding the code gives each
 * page copy that is genuinely its own: the style, the color and the full settings table.
 *
 * Plain ESM with no imports, because two runtimes load it: the React app (via
 * crosshairInfo.d.ts) and web/seo-content.mjs for the first HTML response. Strings come
 * in as arguments from uiStrings.json, so the copy stays in the one place it lives.
 */

/** Index → key in uiStrings `settings.colors`; index 8 is the custom color. */
const COLOR_KEYS = ["white", "green", "yellowGreen", "greenYellow", "yellow", "cyan", "pink", "red"];

function fill(template, values) {
    return String(template ?? "").replace(/\{(\w+)\}/g, (match, key) =>
        key in values ? String(values[key]) : match
    );
}

function num(value, fallback) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
}

/** 0.755 stays 0.755, 1.0000001 becomes 1 — the game itself rounds to three places. */
function fmt(n) {
    return String(Math.round(n * 1000) / 1000);
}

/**
 * A pasted import code cleaned up for the code checker, or null when the text is not one.
 * Players paste codes with stray spaces, line breaks or quotes around them; the game's
 * own codes always start with the format version "0;" and carry the primary profile "P".
 */
export function normalizeCrosshairCode(input) {
    const code = String(input ?? "")
        .replace(/[\s"'`]+/g, "")
        .replace(/;+$/, "");
    if (!/^0(;[A-Za-z0-9.+-]+)+$/.test(code)) return null;
    if (!code.split(";").includes("P")) return null;
    return code;
}

/**
 * Primary-profile settings from a Valorant import code such as
 * `0;P;c;5;h;0;0l;4;0o;2;0a;1;0f;0;1b;0`. Keys the code leaves out take the in-game
 * defaults, which is how the game itself reads a code. ADS and sniper profiles are ignored.
 */
export function decodeCrosshairCode(code) {
    const primary = {};
    const tokens = String(code ?? "").split(";");
    let section = "";
    // tokens[0] is the format version; after it come section markers and key/value pairs.
    for (let i = 1; i < tokens.length; ) {
        const token = tokens[i];
        if (token === "P" || token === "A" || token === "S") {
            section = token;
            i += 1;
            continue;
        }
        if (section === "P" && i + 1 < tokens.length) primary[token] = tokens[i + 1];
        i += 2;
    }

    const get = (key, fallback) => (key in primary ? primary[key] : fallback);

    const line = (prefix, defaults) => {
        const length = num(get(`${prefix}l`), defaults.length);
        const separate = get(`${prefix}g`, "0") === "1";
        return {
            show: get(`${prefix}b`, "1") !== "0",
            thickness: num(get(`${prefix}t`), defaults.thickness),
            length,
            separate,
            vertical: separate ? num(get(`${prefix}v`), length) : length,
            offset: num(get(`${prefix}o`), defaults.offset),
            opacity: num(get(`${prefix}a`), defaults.opacity),
            firingError: get(`${prefix}f`, "1") !== "0",
        };
    };

    // Same rule the preview canvas uses: the custom hex only counts when color 8 is chosen.
    const colorIndex = parseInt(get("c", "0"), 10);
    const hex = get("u", "");
    const customColor = colorIndex === 8 && /^[0-9a-f]{6}/i.test(hex) ? `#${hex.slice(0, 6).toUpperCase()}` : null;

    return {
        colorIndex: colorIndex >= 0 && colorIndex < COLOR_KEYS.length ? colorIndex : 0,
        customColor,
        outlines: { show: get("h", "1") !== "0", thickness: num(get("t"), 1), opacity: num(get("o"), 0.5) },
        centerDot: { show: get("d", "0") === "1", thickness: num(get("z"), 2), opacity: num(get("a"), 1) },
        inner: line("0", { length: 6, thickness: 2, offset: 3, opacity: 0.8 }),
        outer: line("1", { length: 2, thickness: 2, offset: 10, opacity: 0.35 }),
    };
}

/**
 * Whether an element draws anything. Zero length or thickness draws nothing — but zero
 * opacity alone does not: the game still draws the element's outline. Shapes like
 * "Glasses" and "Flower" are built entirely from outlines of invisible lines, and
 * counting them as dot-only put them on the dot crosshair page.
 */
function outlineVisible(outlines) {
    return outlines.show && outlines.opacity > 0 && outlines.thickness > 0;
}

function lineVisible(l, outlines) {
    return l.show && l.thickness > 0 && (l.length > 0 || l.vertical > 0) && (l.opacity > 0 || outlineVisible(outlines));
}

function dotVisible(d, outlines) {
    return d.show && d.thickness > 0 && (d.opacity > 0 || outlineVisible(outlines));
}

/**
 * The crosshair as axis-aligned rectangles in game pixels, centred on (0, 0) — every
 * Valorant crosshair is made of nothing else. Feeds the server-side preview image
 * (web/crosshair-image.mjs), which lets crosshairs appear in image search; the live
 * previews are drawn on <canvas> and are invisible to crawlers.
 *
 * @returns `{ color, fills: [{x, y, w, h, alpha}], outlines: [...same], outlineAlpha }`
 */
export function crosshairShapes(code) {
    const s = decodeCrosshairCode(code);
    const color = s.customColor ?? ["#FFFFFF", "#00FF00", "#7FFF00", "#DFFF00", "#FFFF00", "#00FFFF", "#FF00FF", "#FF0000"][s.colorIndex];
    const fills = [];

    const line = (l) => {
        if (!lineVisible(l, s.outlines)) return;
        const t = l.thickness;
        const { length: h, vertical: v, offset: o } = l;
        if (h > 0) {
            fills.push({ x: o, y: -t / 2, w: h, h: t, alpha: l.opacity });
            fills.push({ x: -o - h, y: -t / 2, w: h, h: t, alpha: l.opacity });
        }
        if (v > 0) {
            fills.push({ x: -t / 2, y: o, w: t, h: v, alpha: l.opacity });
            fills.push({ x: -t / 2, y: -o - v, w: t, h: v, alpha: l.opacity });
        }
    };
    line(s.inner);
    line(s.outer);
    if (dotVisible(s.centerDot, s.outlines)) {
        const z = s.centerDot.thickness;
        fills.push({ x: -z / 2, y: -z / 2, w: z, h: z, alpha: s.centerDot.opacity });
    }

    // An outline is a frame around its element, not a block behind it: with the element at
    // zero opacity the inside stays empty, which is exactly how outline-only shapes such
    // as "Glasses" get their hollow lenses.
    const ot = s.outlines.thickness;
    const outlines = outlineVisible(s.outlines)
        ? fills.flatMap((r) => [
              { x: r.x - ot, y: r.y - ot, w: r.w + 2 * ot, h: ot },
              { x: r.x - ot, y: r.y + r.h, w: r.w + 2 * ot, h: ot },
              { x: r.x - ot, y: r.y, w: ot, h: r.h },
              { x: r.x + r.w, y: r.y, w: ot, h: r.h },
          ])
        : [];
    return { color, fills, outlines, outlineAlpha: s.outlines.opacity };
}

/** Key into uiStrings `settings.styles`. */
export function crosshairStyleKey(settings) {
    const inner = lineVisible(settings.inner, settings.outlines);
    const outer = lineVisible(settings.outer, settings.outlines);
    const dot = dotVisible(settings.centerDot, settings.outlines);
    if (inner && outer) return dot ? "crossOuterDot" : "crossOuter";
    if (inner) return dot ? "crossDot" : "cross";
    if (outer) return dot ? "outerDot" : "outer";
    return dot ? "dot" : "none";
}

/**
 * @param name crosshair name
 * @param code import code
 * @param s uiStrings `settings` block for the page's locale
 * @returns style and color labels, a one-sentence summary, and label/value rows
 */
export function describeCrosshair(name, code, s) {
    const settings = decodeCrosshairCode(code);
    const styleKey = crosshairStyleKey(settings);
    const colorKey = crosshairColorKey(settings);
    const style = s.styles[styleKey];
    const color = settings.customColor ? fill(s.customColor, { hex: settings.customColor }) : s.colors[colorKey];

    const onOff = (b) => (b ? s.on : s.off);
    const pair = (t, o) => fill(s.toggleValue, { thickness: fmt(t), opacity: fmt(o) });
    const lineValue = (l) => {
        if (!l.show) return s.disabled;
        const length = l.separate ? fill(s.lengthSeparate, { h: fmt(l.length), v: fmt(l.vertical) }) : fmt(l.length);
        return fill(s.lineValue, {
            length,
            thickness: fmt(l.thickness),
            offset: fmt(l.offset),
            opacity: fmt(l.opacity),
            firingError: onOff(l.firingError),
        });
    };

    const { outlines, centerDot } = settings;
    // Style and color link to their category page where one exists — the detail page's
    // way into the category, and the category's main source of internal links.
    const pathFor = (field, key) =>
        Object.keys(CATEGORY_PAGES).find((path) => CATEGORY_PAGES[path][field] === key) ?? null;
    return {
        style,
        color,
        summary: fill(s.summary, {
            name,
            style,
            color,
            outlines: outlines.show ? s.withOutlines : s.withoutOutlines,
        }),
        rows: [
            { label: s.labels.style, value: style, href: pathFor("style", styleKey) },
            {
                label: s.labels.color,
                value: color,
                href: pathFor("shade", crosshairShade(settings)) ?? pathFor("color", colorKey),
            },
            { label: s.labels.outlines, value: outlines.show ? pair(outlines.thickness, outlines.opacity) : s.disabled },
            { label: s.labels.centerDot, value: centerDot.show ? pair(centerDot.thickness, centerDot.opacity) : s.disabled },
            { label: s.labels.innerLines, value: lineValue(settings.inner) },
            { label: s.labels.outerLines, value: lineValue(settings.outer) },
        ],
    };
}

/**
 * True when this crosshair's slug carries a uniqueness suffix its name does not explain:
 * "steve" at /steve-272 because another "steve" already held /steve. Both pages would
 * otherwise get the same title — Yandex listed them among its duplicate titles.
 */
function nameCollides(crosshair) {
    const m = /-(\d+)$/.exec(String(crosshair.slug ?? ""));
    if (!m) return false;
    return !new RegExp(`(^|\\D)${m[1]}\\s*$`).test(String(crosshair.name).trim());
}

/**
 * Title, description and keywords for a detail page — the single implementation behind
 * both the server's <head> (web/seo-content.mjs) and the hydrated app (src/seo/pageMeta.ts).
 *
 * @param crosshair `{ name, slug, code, isPro, isFun }`
 * @param d uiStrings `detail` block
 * @param s uiStrings `settings` block
 */
export function buildDetailMeta(crosshair, d, s) {
    const info = describeCrosshair(crosshair.name, crosshair.code, s);
    const vars = { name: crosshair.name, style: info.style, color: info.color };
    const titleName = nameCollides(crosshair) ? `${crosshair.name} (${info.style})` : crosshair.name;
    const description = [
        fill(d.description, vars),
        crosshair.isPro ? d.pro : null,
        crosshair.isFun ? d.fun : null,
        d.outro,
    ]
        .filter(Boolean)
        .join(" ");

    return {
        title: fill(d.title, { name: titleName }),
        description,
        keywords: fill(d.keywords, vars),
    };
}

/**
 * Catalog pages built from the decoded code rather than from a database flag. Searches
 * like "прицел точка валорант" (305 Yandex impressions a month) and "valorant crosshair
 * renk kodları" had no page to land on. Pink (8 crosshairs) is left out: a page that
 * thin would hurt more than help.
 */
export const CATEGORY_PAGES = {
    "/valorant-dot-crosshairs": { style: "dot" },
    "/valorant-white-crosshairs": { color: "white" },
    "/valorant-green-crosshairs": { color: "green" },
    "/valorant-cyan-crosshairs": { color: "cyan" },
    "/valorant-red-crosshairs": { color: "red" },
    "/valorant-yellow-crosshairs": { color: "yellow" },
    "/valorant-custom-color-crosshairs": { color: "custom" },
    // Shade pages also take hex colors of that hue, so they overlap the custom-color page.
    "/valorant-pink-crosshairs": { color: "pink", shade: "pink" },
    "/valorant-black-crosshairs": { shade: "black" },
};

export const CATEGORY_PAGE_SIZE = 30;

/** `settings.colors` key, or "custom" for a hex color. */
export function crosshairColorKey(settings) {
    return settings.customColor ? "custom" : COLOR_KEYS[settings.colorIndex];
}

/**
 * The named shade of a hex color, for the shade pages: "black" for near-black, "pink" for
 * pink and magenta hues. Null for preset colors and every other hex color.
 */
export function crosshairShade(settings) {
    const hex = (settings.customColor || "").replace("#", "");
    if (hex.length < 6) return null;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max < 0.2) return "black";
    const light = (max + min) / 2;
    const chroma = max - min;
    const saturation = chroma === 0 ? 0 : chroma / (1 - Math.abs(2 * light - 1));
    if (saturation < 0.15) return null;
    let hue;
    if (max === r) hue = ((g - b) / chroma) % 6;
    else if (max === g) hue = (b - r) / chroma + 2;
    else hue = (r - g) / chroma + 4;
    hue = (hue * 60 + 360) % 360;
    // Magenta through rose; light reds (salmon, #F28D9F) read as pink too.
    if (hue >= 290 && hue < 345) return "pink";
    if ((hue >= 345 || hue < 15) && light > 0.7) return "pink";
    return null;
}

/** Category page paths (unlocalized) this code belongs to. */
export function categoriesOf(code) {
    const settings = decodeCrosshairCode(code);
    const style = crosshairStyleKey(settings);
    const color = crosshairColorKey(settings);
    const shade = crosshairShade(settings);
    return Object.keys(CATEGORY_PAGES).filter((path) => {
        const rule = CATEGORY_PAGES[path];
        if (rule.style) return rule.style === style;
        return rule.color === color || (rule.shade != null && rule.shade === shade);
    });
}

/**
 * One page of a category, from the full approved list (already in catalog order).
 * @returns `{ items, page, totalPages }` — totalPages is at least 1, as for the API lists
 */
export function categoryPage(path, all, page) {
    const matching = (all || []).filter((c) => categoriesOf(c.code).includes(path));
    const totalPages = Math.max(1, Math.ceil(matching.length / CATEGORY_PAGE_SIZE));
    const start = (page - 1) * CATEGORY_PAGE_SIZE;
    return { items: matching.slice(start, start + CATEGORY_PAGE_SIZE), page, totalPages };
}

/** Heading over the related-crosshair links, by the crosshair's own category. */
export function relatedHeading(crosshair, d) {
    if (crosshair.isPro) return d.relatedPro;
    if (crosshair.isFun) return d.relatedFun;
    return d.relatedOther;
}

/**
 * "Team: T1. Code checked against prosettings.net on 5 November 2026." for a pro
 * crosshair page. `roster` is src/seo/proPlayers.json; the caller passes it in because
 * the server loads this module from source text, where a JSON import is not possible.
 * Null when the player is not in the roster.
 */
export function proPlayerNote(slug, roster, d, locale) {
    const player = roster && roster.players ? roster.players[slug] : null;
    if (!player) return null;
    const statuses = {
        "Free Agent": d.proFreeAgent,
        Retired: d.proRetired,
        "Content Creator": d.proContentCreator,
    };
    const parts = [];
    if (player.team) parts.push(fill(d.proTeam, { team: statuses[player.team] ?? player.team }));
    if (player.checked) {
        const date = new Intl.DateTimeFormat(locale, {
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: "UTC",
        }).format(new Date(`${player.checked}T00:00:00Z`));
        parts.push(fill(d.proChecked, { source: roster.source, date }));
    }
    return parts.length ? parts.join(" ") : null;
}
